import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../../../utils/api";

export const useTopicBrief = (query, country, dateRange, auto, enabled = true) => {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null), [error, setError] = useState(""), [locked, setLocked] = useState(false), [busy, setBusy] = useState(false);
  const revision = useRef(0), started = useRef(new Set()), timer = useRef(null);
  const key = JSON.stringify([query, country, dateRange, user?.user_id]);
  const url = `/api/search/topic?${new URLSearchParams({ q: query, ...(country ? { country } : {}), date_range: dateRange || "all" })}`;
  const load = useCallback(async () => (await apiFetch(url)).data, [url]);
  const watch = useCallback(async (first, epoch) => {
    if (epoch !== revision.current) return;
    setData(first); setBusy(first.status === "pending");
    if (first.status === "pending") timer.current = setTimeout(async () => {
      try { const next = await load(); if (epoch === revision.current) watch(next, epoch); }
      catch { if (epoch === revision.current) { setError("The update check was interrupted. Reload the saved state before retrying."); setBusy(false); } }
    }, 1800);
  }, [load]);
  const prepare = useCallback(async (refresh = false) => {
    const epoch = revision.current; setBusy(true); setError(""); clearTimeout(timer.current);
    try { const response = await apiFetch("/api/search/topic/prepare", { method: "POST", body: JSON.stringify({ query, country: country || null, date_range: dateRange || "all", refresh }) }); watch(response.data, epoch); }
    catch (err) { if (epoch === revision.current) { setLocked(err.status === 402); setError(typeof err.data?.detail === "string" ? err.data.detail : "Research could not be started. Please retry."); setBusy(false); } }
  }, [query, country, dateRange, watch]);
  useEffect(() => {
    const epoch = ++revision.current; clearTimeout(timer.current); setData(null); setError(""); setLocked(false); setBusy(false);
    if (!enabled) { setData({ status: "scope_unavailable" }); return; }
    if (loading || query.trim().length < 2) return;
    if (!user) { setLocked(true); return; }
    load().then(next => {
      if (epoch !== revision.current) return;
      watch(next, epoch);
      if (auto && next.status === "not_prepared" && !started.current.has(key)) { started.current.add(key); prepare(); }
    }).catch(err => { if (epoch === revision.current) { setLocked(err.status === 402); if (err.status !== 402) setError("Saved topic research could not be loaded."); } });
    return () => { ++revision.current; clearTimeout(timer.current); };
  }, [key, query, loading, user, auto, enabled, load, prepare, watch]);
  const selectEdition = async id => {
    const epoch = revision.current;
    try { const result = await apiFetch(`/api/search/topic/editions/${encodeURIComponent(id)}`); if (epoch === revision.current) setData(previous => ({ ...previous, edition: result.data })); }
    catch { setError("This edition could not be loaded."); }
  };
  return { data, error, locked, busy, prepare, selectEdition };
};